import { Spin } from "antd";
import { type FC, useMemo, useRef } from "react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";

import { acceptedModeMapper } from "src/app/features/Contract/services/mappers/ContractDtoToEntity";
import type { ContractAppProfileEntity } from "src/app/features/Contract/types/subTypes/ContractAppProfileEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import type { AntCustomFormField, CommonFieldAttributes, SelectDynamicFormField } from "../../types/types";
import { Checkbox } from "../Checkbox/Checkbox";
import { NumberInput } from "../Number/NumberInput";
import { SelectDynamic } from "../SelectDynamic/SelectDynamic";
import { TextInput } from "../Text/TextInput";
import { useLazyLoadAcceptedCardProfilesQuery } from "./ContractsAcceptedCardTypesServerApi";
import type { LoadContractsAppProfilesRequest } from "./types";

export interface ContractsAppProfilesTableProps extends AntCustomFormField<ContractAppProfileEntity[]>, CommonFieldAttributes {
    contractProfileId: number;
    appProfileListId: number;
    id?: number;
    resourceType?: string;
    acquirerId?: number;
}

type ComponentState = {
    contractProfileId?: number;
    appProfileListId?: number;
    shouldUpdateData: boolean;
    queryConfig?: LoadContractsAppProfilesRequest;
};
export const ContractsAcceptedCardTypesTable: FC<ContractsAppProfilesTableProps> = ({
    value = [],
    onChange,
    contractProfileId,
    id,
    appProfileListId,
    resourceType,
    acquirerId,
}) => {
    // TODO consider separate tables for contract and contract profile
    const [state, setState] = useState<ComponentState>({
        shouldUpdateData: false,
        appProfileListId,
        contractProfileId,
    });
    const [execQuery, { isFetching }] = useLazyLoadAcceptedCardProfilesQuery();

    const location = useLocation();
    const isEdit = location.pathname.includes("/edit");

    const dataGroup = getAssignedDataGroup();
    const dataGroupCode = useMemo(() => dataGroup.code, [dataGroup]);
    const isResourceUpdate = Number.isInteger(id);

    const isDataGroupApac = dataGroup.formConfigName === "APAC";
    const isDataGroupIpaso = dataGroup.formConfigName === "IPASO";

    // Use ref to store the latest onChange callback
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Handle data changes
    const onDataChanged = useCallback((tableValue: ContractAppProfileEntity[]) => {
        if (onChangeRef.current) {
            onChangeRef.current(tableValue);
        }
    }, []);

    const onInput = useCallback(
        (cellId: string | number, key: keyof ContractAppProfileEntity, newValue: string | boolean) => {
            const newData = value.map((row) => {
                if (row.cardTypeId !== cellId) return row;
                return { ...row, [key]: newValue };
            });
            onDataChanged(newData);
        },
        [value, onDataChanged],
    );

    const getColumnsConfig = useCallback(
        (t: Function): ExtendedColumnsType<ContractAppProfileEntity>[] => {
            return [
                {
                    title: t("genericTable.column.id"),
                    key: "cardTypeId",
                    dataIndex: "cardTypeId",
                },
                {
                    title: t("form.readOnly"),
                    key: "readOnly",
                    dataIndex: "readOnly",
                    hidden: resourceType === "contracts",
                    render: (_, record) => (
                        <Checkbox
                            name={`readOnly-${record.cardTypeId}`}
                            label=""
                            value={record.readOnly ?? false}
                            readOnly={false}
                            onChange={(checked) => onInput(record.cardTypeId!, "readOnly", checked)}
                        />
                    ),
                },
                {
                    title: t("applicationProfileCreateForm.table.cardType"),
                    key: "cardTypeName",
                    dataIndex: "cardTypeName",
                },
                {
                    title: t("contractCreateForm.acceptMode"),
                    key: "acceptMode",
                    dataIndex: "acceptMode",
                    render: (_, record) => (
                        <SelectDynamic
                            name={`acceptMode-${record.cardTypeId}`}
                            label=""
                            value={record.acceptMode}
                            readOnly={false}
                            onChange={(newValue) => onInput(record.cardTypeId!, "acceptMode", newValue)}
                            fieldConfig={
                                {
                                    urlToFetchOptions: isDataGroupApac ? "/configurations/accept-mode-apac" : "/configurations/accept-mode",
                                    optionDisplayPropName: "key",
                                    optionValuePropName: "value",
                                } as SelectDynamicFormField
                            }
                        />
                    ),
                },
                {
                    title: t("contractCreateForm.acceptMode.routingID"),
                    key: "routingId",
                    dataIndex: "routingId",
                    hidden: isDataGroupApac,
                    render: (_, record) => (
                        <NumberInput
                            name={`routingId-${record.cardTypeId}`}
                            label=""
                            value={record.routingId?.toString()}
                            readOnly={false}
                            onChange={(newValue) => onInput(record.cardTypeId!, "routingId", newValue)}
                        />
                    ),
                },
                {
                    title: t("contractCreateForm.acceptMode.merchantId"),
                    key: "merchantId",
                    dataIndex: "merchantId",
                    hidden: isDataGroupApac || resourceType === "contract-profiles",
                    render: (_, record) => (
                        <TextInput
                            name={`merchantId-${record.cardTypeId}`}
                            label=""
                            value={record.merchantId}
                            readOnly={false}
                            onChange={(newValue) => onInput(record.cardTypeId!, "merchantId", newValue)}
                        />
                    ),
                },
                {
                    title: t("contractCreateForm.acceptMode.hostOperator"),
                    key: "hostOperatorId",
                    dataIndex: "hostOperatorId",
                    hidden: !isDataGroupIpaso,
                    render: (_, record) =>
                        acquirerId && (
                            <SelectDynamic
                                name={`hostOperatorId-${record.cardTypeId}`}
                                label=""
                                value={record.hostOperatorId?.toString()}
                                readOnly={false}
                                onChange={(newValue) => onInput(record.cardTypeId!, "hostOperatorId", newValue)}
                                fieldConfig={
                                    {
                                        urlToFetchOptions: `/configurations/host-operator?acquirer-id=${acquirerId}`,
                                        optionDisplayPropName: "key",
                                        optionValuePropName: "value",
                                    } as SelectDynamicFormField
                                }
                            />
                        ),
                },
            ];
        },
        [acquirerId, isDataGroupApac, isDataGroupIpaso, onInput, resourceType],
    );

    // Watch for prop changes and update state
    useEffect(() => {
        // Check if is it initial render on update?
        // BE query should not be called when values are initialised on initial render
        // if so: set state but do not call the BE
        if (state.appProfileListId === undefined && state.contractProfileId === undefined && isEdit) {
            setState({
                appProfileListId,
                contractProfileId,
                shouldUpdateData: false,
            });
            return;
        }

        const appProfileIdHasChanged = state.appProfileListId !== appProfileListId;
        const contractProfileIdHasChanged = state.contractProfileId !== contractProfileId;
        const shouldUpdateData = !!appProfileListId && (appProfileIdHasChanged || contractProfileIdHasChanged);

        if (shouldUpdateData) {
            const siteParams = isResourceUpdate && resourceType === "contracts" ? `${id}/` : ``;
            const appProfileString = appProfileListId ? `?appProfileListId=${appProfileListId}` : ``;
            const contractProfilesCaseString = isResourceUpdate ? `&contractProfileId=${id}` : ``;
            const contractsCaseString = contractProfileId ? `&contractProfileId=${contractProfileId}` : ``;
            const contractProfileString =
                resourceType === "contracts" ? `${contractsCaseString}&isContract=true` : `${contractProfilesCaseString}&isContract=false`;
            const queryParams = `${appProfileString}${contractProfileString}`;
            const queryConfig = { siteParams, dataGroupCode, queryParams };

            setState({
                appProfileListId,
                contractProfileId,
                queryConfig,
                shouldUpdateData,
            });
        } else if (appProfileIdHasChanged || contractProfileIdHasChanged) {
            // Values changed but no query needed (e.g., appProfileListId was cleared).
            // Keep state in sync so future change detection stays accurate.
            setState({
                appProfileListId,
                contractProfileId,
                shouldUpdateData: false,
            });
        }
    }, [
        appProfileListId,
        contractProfileId,
        dataGroupCode,
        id,
        isEdit,
        isResourceUpdate,
        resourceType,
        state.appProfileListId,
        state.contractProfileId,
    ]);

    // Execute query when state is ready
    useEffect(() => {
        let isCancelled = false; // Track if the current request should be ignored

        if (state.shouldUpdateData && state.queryConfig) {
            execQuery(state.queryConfig)
                .unwrap()
                .then((data) => {
                    if (!isCancelled) {
                        const mappedData = data.items.length > 0 ? data.items.map(acceptedModeMapper) : [];
                        onDataChanged(mappedData);
                        setState((prevState) => ({ ...prevState, shouldUpdateData: false })); // Reset the flag
                    }
                })
                .catch(() => {
                    if (!isCancelled) {
                        setState((prevState) => ({ ...prevState, shouldUpdateData: false })); // Reset the flag
                    }
                });
        }

        return () => {
            isCancelled = true; // Cancel the current request on cleanup
        };
    }, [execQuery, onDataChanged, state.queryConfig, state.shouldUpdateData]);

    return (
        <Spin spinning={isFetching}>
            <PropertyTable<ContractAppProfileEntity>
                name="contractsAcceptedCardTypes_table"
                rowsAreSelectable={false}
                getColumnsConfig={getColumnsConfig}
                propertyAsRowKey="cardTypeId"
                tableData={value}
                readOnly={false}
                readOnlyVisibilityEnabled={resourceType === "contracts"}
                onDataChanged={onDataChanged}
            />
        </Spin>
    );
};
